import {
  ChevronRight,
  Briefcase,
  Headphones,
  Handshake,
  Building2,
  Landmark
} from 'lucide-react';

// La corporate d'une plateforme est toujours créée automatiquement à la
// création de la plateforme ; « compagnie » crée ici une CIE autonome
// (porteur de risque, rattachable à des plateformes ensuite).
export type PersonneMoraleType =
  | 'courtier'
  | 'gestionnaire'
  | 'mia'
  | 'pim'
  | 'compagnie';

interface Props {
  onClose: () => void;
  onSelect: (type: PersonneMoraleType) => void;
}

interface TypeOption {
  type: PersonneMoraleType;
  title: string;
  description: string;
  icon: JSX.Element;
  gradient: string;
  bgColor: string;
  hoverBg: string;
  disabled?: boolean;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    type: 'courtier',
    title: 'Courtier',
    description:
      "Intermédiaire en assurance immatriculé à l'ORIAS qui distribue des contrats pour ses clients.",
    icon: <Briefcase className="w-8 h-8" />,
    gradient: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    hoverBg: 'hover:bg-blue-100'
  },
  {
    type: 'gestionnaire',
    title: 'Gestionnaire',
    description:
      'Société de gestion de contrats et sinistres. Immatriculation ORIAS facultative.',
    icon: <Headphones className="w-8 h-8" />,
    gradient: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    hoverBg: 'hover:bg-emerald-100'
  },
  {
    type: 'mia',
    title: 'MIA',
    description:
      "Mandataire d'Intermédiaire en Assurance : devise sous les produits de ses courtiers mandants.",
    icon: <Handshake className="w-8 h-8" />,
    gradient: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    hoverBg: 'hover:bg-orange-100'
  },
  {
    type: 'pim',
    title: "Prof. de l'immobilier",
    description:
      "Professionnel de l'immobilier : devise sous les produits de ses courtiers mandants (comme un MIA, sans ORIAS).",
    icon: <Building2 className="w-8 h-8" />,
    gradient: 'from-violet-500 to-purple-500',
    bgColor: 'bg-violet-50',
    hoverBg: 'hover:bg-violet-100'
  },
  {
    type: 'compagnie',
    title: 'Compagnie',
    description:
      "Compagnie d'assurance : porteur de risque des garanties, peut gouverner une plateforme. Pas d'ORIAS (agrément ACPR).",
    icon: <Landmark className="w-8 h-8" />,
    gradient: 'from-slate-600 to-gray-700',
    bgColor: 'bg-slate-50',
    hoverBg: 'hover:bg-slate-100'
  }
];

export const NewPersonneMoraleTypeModal: React.FC<Props> = ({
  onClose,
  onSelect
}) => {
  const handleSelect = (option: TypeOption) => {
    if (option.disabled) return;
    onSelect(option.type);
    onClose();
  };

  return (
    <div className="max-w-6xl mx-auto p-10">
      <div className="text-center mt-2 mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Ajouter une personne morale
        </h2>
        <p className="text-gray-600 text-lg">
          Choisissez le type de personne morale à créer.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {TYPE_OPTIONS.map(option => {
          const isDisabled = !!option.disabled;
          const baseClasses = `group relative rounded-2xl border-2 border-gray-100 ${option.bgColor}
                            transition-all duration-300 ease-in-out transform`;
          const interactiveClasses = isDisabled
            ? 'opacity-50 cursor-not-allowed grayscale'
            : `cursor-pointer ${option.hoverBg} hover:scale-[1.02] hover:shadow-xl shadow-lg`;

          return (
            <div
              key={option.type}
              className={`${baseClasses} ${interactiveClasses}`}
              onClick={() => handleSelect(option)}
              role="button"
              aria-disabled={isDisabled}
              tabIndex={isDisabled ? -1 : 0}
            >
              {!isDisabled && (
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${option.gradient} opacity-0 group-hover:opacity-5
                           rounded-2xl transition-opacity duration-300`}
                />
              )}

              <div className="relative h-full p-6 flex items-center gap-5">
                <div
                  className={`flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-full
                           bg-gradient-to-br ${
                             option.gradient
                           } text-white shadow-lg
                           ${
                             isDisabled
                               ? ''
                               : 'group-hover:shadow-xl group-hover:scale-110'
                           }
                           transition-all duration-300`}
                >
                  {option.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {option.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed">
                    {option.description}
                  </p>
                </div>

                {isDisabled ? (
                  <span className="flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                    Bientôt disponible
                  </span>
                ) : (
                  <ChevronRight
                    className="flex-shrink-0 w-6 h-6 text-gray-400 group-hover:text-gray-700
                             transition-all duration-300 group-hover:translate-x-1"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
